import { api, setUnauthorizedHandler, type SessionResponse } from '../api/client';
import { applyI18n } from '../i18n';
import { clearStoredVaultVersion } from '../crypto/vault';
import { clearVaultSession, hasVaultSession } from '../crypto/vaultSession';
import { clearGuestSessionSecrets } from '../crypto/clearGuestSessionSecrets';
import { AppShell, type GuestShellModule } from './shell';
import { DevLoginPage } from '../pages/DevLoginPage';
import { HomePage } from '../pages/HomePage';
import { FilesPage } from '../pages/FilesPage';
import { GuestSharedFolderPage } from '../pages/GuestSharedFolderPage';
import { SmsPage } from '../pages/SmsPage';
import { InformationPage, disposeInformationPageLive } from '../pages/InformationPage';
import { GuestInfoSharePage } from '../pages/GuestInfoSharePage';
import { VaultSetupPage } from '../pages/VaultSetupPage';
import { VaultUnlockPage } from '../pages/VaultUnlockPage';
import { SettingsPage } from '../pages/SettingsPage';
import { AdminPage } from '../pages/AdminPage';
import { SedrModulePage } from '../pages/sedr/SedrModulePage';
import { GuestSedrPage } from '../pages/GuestSedrPage';
import { GuestPortalPage } from '../pages/GuestPortalPage';
import { QuickSharePage } from '../pages/QuickSharePage';
import { HelpPage } from '../pages/HelpPage';
import { SignedOutPage } from '../pages/SignedOutPage';
import { guestApi } from '../api/guest';
import { subscribeVaultRecoverySetup, isVaultRecoverySetupPending } from './vaultSetupRecovery';
import { isAdminModulePath, isGuestSettingsPath, isSedrLibraryPath, isSettingsModulePath, sedrRoomUrlToGuestRoomUrl } from './navigationState';
import { isVaultSessionUnlocked } from './vaultSessionGate';
import { stripSecretQueryParams } from './stripSecretQueryParams';

export type AppState =
  | { kind: 'loading' }
  | { kind: 'anonymous' }
  | { kind: 'authenticated'; session: SessionResponse; vaultUnlocked: boolean };

export { isVaultSessionUnlocked } from './vaultSessionGate';

/** Routes reachable without vault setup or unlock (SMS, Administration). */
const VAULT_FREE_ROUTES = new Set(['/sms']);

let appState: AppState = { kind: 'loading' };
const listeners = new Set<() => void>();

export function getAppState(): AppState {
  return appState;
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setAppState(next: AppState): void {
  appState = next;
  listeners.forEach((listener) => listener());
}

function sessionsMatch(a: SessionResponse, b: SessionResponse): boolean {
  return (
    a.userId === b.userId
    && a.displayName === b.displayName
    && a.email === b.email
    && a.vaultSetupComplete === b.vaultSetupComplete
    && a.vaultVersion === b.vaultVersion
    && a.roles.length === b.roles.length
    && a.roles.every((role, index) => role === b.roles[index])
  );
}

/** Drop authenticated UI state after a 401 (cookie session gone). Guest 401s no-op. */
export function handleUnauthorized(): void {
  if (appState.kind !== 'authenticated') {
    return;
  }
  clearVaultSession();
  clearGuestSessionSecrets();
  setAppState({ kind: 'anonymous' });
}

export async function refreshSession(): Promise<void> {
  try {
    const session = await api.getSession();
    if (!session.vaultSetupComplete) {
      clearStoredVaultVersion(session.userId);
    }

    const { applyPreferredLocale } = await import('../i18n');
    await applyPreferredLocale(session.locale);

    const vaultUnlocked = isVaultSessionUnlocked(
      session.vaultSetupComplete,
      hasVaultSession(),
    );
    if (
      appState.kind === 'authenticated'
      && appState.vaultUnlocked === vaultUnlocked
      && sessionsMatch(appState.session, session)
    ) {
      return;
    }
    setAppState({
      kind: 'authenticated',
      session,
      vaultUnlocked,
    });
  } catch (cause) {
    console.error('Session refresh failed', cause);
    setAppState({ kind: 'anonymous' });
  }
}

export function markVaultUnlocked(): void {
  if (appState.kind !== 'authenticated') return;
  setAppState({ ...appState, vaultUnlocked: true });
}

let logoutInFlight = false;

export async function logout(): Promise<void> {
  if (logoutInFlight) return;
  logoutInFlight = true;
  setAppState({ kind: 'loading' });
  try {
    await api.logout();
  } catch (cause) {
    console.error('Logout failed', cause);
  } finally {
    clearVaultSession();
    clearGuestSessionSecrets();
    guestSessionEnded = false;
    history.replaceState({}, '', '/signed-out');
    setAppState({ kind: 'anonymous' });
    logoutInFlight = false;
  }
}

let guestLogoutInFlight = false;
/** After guest logout, keep showing signed-out confirmation on the guest URL. */
let guestSessionEnded = false;

function guestModuleForPath(path: string): GuestShellModule {
  if (isGuestFolderRoute(path)) return 'files';
  if (isGuestSedrRoute(path)) return 'sedr';
  return 'information';
}

function renderGuestSignedOut(root: HTMLElement, path: string): void {
  root.replaceChildren(
    AppShell(SignedOutPage(), {
      guestModule: guestModuleForPath(path),
      signedOut: true,
    }),
  );
}

/** End the guest session; stay on the guest URL and show signed-out confirmation. */
export async function logoutGuest(): Promise<void> {
  if (guestLogoutInFlight) return;
  guestLogoutInFlight = true;
  // Set before clearing DOM so a concurrent applyRoute cannot remount the guest
  // page (missing .app-shell--guest) and restart gate/verify while logout runs.
  guestSessionEnded = true;

  const root = document.getElementById('app');
  if (root) {
    const loading = document.createElement('div');
    loading.className = 'center-panel';
    loading.innerHTML = `<p data-i18n="status.loading"></p>`;
    applyI18n(loading);
    root.replaceChildren(loading);
  }

  try {
    if (isGuestPortalRoute(getPath())) {
      clearVaultSession();
      await guestApi.logout();
    } else {
      await api.guestLogout();
    }
  } catch (cause) {
    console.error('Guest logout failed', cause);
  } finally {
    clearGuestSessionSecrets();
    clearVaultSession();
    guestLogoutInFlight = false;
    if (root) {
      renderGuestSignedOut(root, getPath());
    }
  }
}

function getPath(): string {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

function isGuestRoute(path: string): boolean {
  return /^\/i\/[^/]+$/.test(path);
}

function isGuestFolderRoute(path: string): boolean {
  return /^\/f\/[^/]+\/g\/[^/]+$/.test(path);
}

function isGuestSedrRoute(path: string): boolean {
  return /^\/s\/[^/]+\/g\/[^/]+$/.test(path);
}

function isGuestPortalRoute(path: string): boolean {
  return path === '/guest' || path.startsWith('/guest/');
}

/** Guest home or room detail — keeps the animated SEDR workspace mounted. */
function isGuestSedrWorkspacePath(path: string): boolean {
  const normalized = path.replace(/\/$/, '') || '/guest';
  if (normalized === '/guest') return true;
  return /^\/guest\/rooms\/[^/]+(\/(dashboard|compliance|evidence|audit)(\/[^/]+)?)?$/.test(normalized);
}

function isQuickShareRoute(path: string): boolean {
  return path === '/share' || path === '/share/';
}

function isGuestAppPath(path: string): boolean {
  return isGuestRoute(path)
    || isGuestFolderRoute(path)
    || isGuestSedrRoute(path)
    || isGuestPortalRoute(path)
    || isQuickShareRoute(path);
}

function isFilesFolderRoute(path: string): boolean {
  return /^\/files\/[^/]+$/.test(path);
}

function isSedrRoomRoute(path: string): boolean {
  if (path === '/sedr/library' || path.startsWith('/sedr/library/')) return false;
  return /^\/sedr\/[^/]+(\/(phases|compliance|evidence|access|reports|audit|settings)(\/[^/]+)?)?$/.test(path);
}

function isVaultFreeRoute(path: string): boolean {
  return VAULT_FREE_ROUTES.has(path) || isAdminModulePath(path);
}

function renderRoute(): HTMLElement {
  const state = getAppState();
  const path = getPath();

  if (state.kind === 'loading') {
    const loading = document.createElement('div');
    loading.className = 'center-panel';
    loading.innerHTML = `<p data-i18n="status.loading"></p>`;
    applyI18n(loading);
    return loading;
  }

  if (state.kind === 'anonymous') {
    if (path === '/help') {
      return HelpPage();
    }

    if (path === '/signed-out') {
      return SignedOutPage({ showSignIn: true });
    }

    if (import.meta.env.DEV && (path === '/login' || path === '/')) {
      return DevLoginPage();
    }

    const panel = document.createElement('div');
    panel.className = 'center-panel';
    if (import.meta.env.DEV) {
      panel.innerHTML = `
        <h1 data-i18n="auth.login"></h1>
        <p class="muted" data-i18n="home.subtitle"></p>
      `;
      const link = document.createElement('a');
      link.href = '/login';
      link.className = 'button primary';
      link.innerHTML = `<span data-i18n="auth.devLogin.title"></span>`;
      panel.appendChild(link);
    } else {
      // Production: SAML is the only auth — send the browser straight to the IdP.
      const returnUrl = !path || path === '/login' ? '/' : path;
      const samlUrl = `/api/auth/saml/login?returnUrl=${encodeURIComponent(returnUrl)}`;
      panel.innerHTML = `
        <h1 data-i18n="auth.login"></h1>
        <p class="muted" data-i18n="status.loading"></p>
      `;
      window.location.replace(samlUrl);
    }
    applyI18n(panel);
    return panel;
  }

  if (path === '/help') {
    return HelpPage();
  }

  if (isVaultFreeRoute(path)) {
    return renderModuleRoute(path);
  }

  if (!state.session.vaultSetupComplete) {
    return VaultSetupPage();
  }

  if (isVaultRecoverySetupPending()) {
    return VaultSetupPage();
  }

  if (!state.vaultUnlocked) {
    return VaultUnlockPage();
  }

  return renderModuleRoute(path);
}

function renderModuleRoute(path: string): HTMLElement {
  switch (path) {
    case '/help':
      return HelpPage();
    case '/information':
      return InformationPage();
    case '/files':
      return FilesPage();
    case '/sedr':
      return SedrModulePage();
    case '/sms':
      return SmsPage();
    default:
      if (isSettingsModulePath(path)) {
        return SettingsPage();
      }
      if (isAdminModulePath(path)) {
        return AdminPage();
      }
      if (isFilesFolderRoute(path)) {
        return FilesPage();
      }
      if (isSedrLibraryPath(path) || isSedrRoomRoute(path)) {
        return SedrModulePage();
      }
      return HomePage();
  }
}

function isFilesModulePath(path: string): boolean {
  return path === '/files' || isFilesFolderRoute(path);
}

/** Soft in-place nav between list, room, and library views. */
function isSedrWorkspacePath(path: string): boolean {
  return path === '/sedr' || isSedrRoomRoute(path) || isSedrLibraryPath(path);
}

function applyRoute(root: HTMLElement, previousPath: string): void {
  const path = getPath();
  if (!path.startsWith('/information')) {
    disposeInformationPageLive();
  }

  // Public help page (no vault / auth gate).
  if (path === '/help') {
    const state = getAppState();
    if (state.kind === 'authenticated') {
      root.replaceChildren(AppShell(HelpPage()));
    } else {
      root.replaceChildren(HelpPage());
    }
    return;
  }

  if (path === '/signed-out') {
    root.replaceChildren(AppShell(SignedOutPage({ showSignIn: true })));
    return;
  }

  if (isQuickShareRoute(path)) {
    if (previousPath === path && root.querySelector('.quick-share-page')) {
      return;
    }
    root.replaceChildren(AppShell(QuickSharePage(), { guestDisplayName: '', guestModule: 'files' }));
    return;
  }
  if (isGuestRoute(path)) {
    if (guestSessionEnded) {
      renderGuestSignedOut(root, path);
      return;
    }
    // Same-path re-renders (session/subscribe) must not remount guest pages:
    // passwordless auto-verify races single-use gate nonces across mounts.
    if (previousPath === path && root.querySelector('.app-shell--guest')) {
      return;
    }
    root.replaceChildren(AppShell(GuestInfoSharePage(), { guestDisplayName: '', guestModule: 'information' }));
    return;
  }
  if (isGuestFolderRoute(path)) {
    if (guestSessionEnded) {
      renderGuestSignedOut(root, path);
      return;
    }
    if (previousPath === path && root.querySelector('.app-shell--guest')) {
      return;
    }
    root.replaceChildren(AppShell(GuestSharedFolderPage(), { guestDisplayName: '', guestModule: 'files' }));
    return;
  }
  if (isGuestSedrRoute(path)) {
    if (guestSessionEnded) {
      renderGuestSignedOut(root, path);
      return;
    }
    if (previousPath === path && root.querySelector('.app-shell--guest')) {
      return;
    }
    root.replaceChildren(AppShell(GuestSedrPage(), { guestDisplayName: '', guestModule: 'sedr' }));
    return;
  }
  if (isGuestPortalRoute(path)) {
    if (
      isGuestSettingsPath(previousPath)
      && isGuestSettingsPath(path)
    ) {
      const settingsWorkspace = root.querySelector('[data-guest-settings-workspace]');
      if (settingsWorkspace) {
        settingsWorkspace.dispatchEvent(new Event('guest-settings:route'));
        return;
      }
    }
    if (
      isGuestSedrWorkspacePath(previousPath)
      && isGuestSedrWorkspacePath(path)
    ) {
      const workspace = root.querySelector('[data-guest-sedr-workspace]');
      if (workspace) {
        workspace.dispatchEvent(new Event('guest-sedr:route'));
        return;
      }
    }
    if (previousPath === path) {
      if (root.querySelector('[data-guest-settings-workspace]')) return;
      if (root.querySelector('[data-guest-sedr-workspace]')) return;
      if (root.querySelector('[data-guest-account-sedr]')) return;
      if (root.querySelector('.guest-portal, .module-page--guest-portal')) return;
    }
    root.replaceChildren(
      AppShell(GuestPortalPage(), {
        guestDisplayName: '',
        guestModule: 'sedr',
        guestAccount: true,
      }),
    );
    return;
  }

  if (
    isFilesModulePath(previousPath)
    && isFilesModulePath(path)
  ) {
    const workspace = root.querySelector('[data-files-workspace]');
    if (workspace) {
      // Keep FilesPage mounted for share switches and query-only path changes
      // (`/files/{id}?path=...`). Remounting here wiped the explorer on every
      // subfolder click — SEDR avoids that by encoding folder state in the pathname.
      workspace.dispatchEvent(new Event('files:route'));
      return;
    }
  }

  if (
    isSedrWorkspacePath(previousPath)
    && isSedrWorkspacePath(path)
  ) {
    const workspace = root.querySelector('[data-sedr-workspace]');
    if (workspace) {
      workspace.dispatchEvent(new Event('sedr:route'));
      return;
    }
  }

  if (
    isAdminModulePath(previousPath)
    && isAdminModulePath(path)
  ) {
    const workspace = root.querySelector('[data-admin-workspace]');
    if (workspace) {
      workspace.dispatchEvent(new Event('admin:route'));
      return;
    }
  }

  if (
    isSettingsModulePath(previousPath)
    && isSettingsModulePath(path)
  ) {
    const workspace = root.querySelector('[data-settings-workspace]');
    if (workspace) {
      workspace.dispatchEvent(new Event('settings:route'));
      return;
    }
  }

  // Shared SEDR deep links: guests with a session are remapped into /guest/rooms/...
  if (isSedrRoomRoute(path) && getAppState().kind !== 'authenticated') {
    if (root.querySelector('[data-guest-sedr-bridge]')) {
      return;
    }
    const guestUrl = sedrRoomUrlToGuestRoomUrl(path);
    if (guestUrl) {
      const loading = document.createElement('div');
      loading.className = 'center-panel';
      loading.dataset.guestSedrBridge = '';
      loading.innerHTML = `<p data-i18n="status.loading"></p>`;
      applyI18n(loading);
      root.replaceChildren(loading);
      void guestApi.session()
        .then(() => {
          history.replaceState({}, '', guestUrl);
          window.dispatchEvent(new PopStateEvent('popstate'));
        })
        .catch(() => {
          root.replaceChildren(AppShell(renderRoute()));
        });
      return;
    }
  }

  // Same-path re-renders (session/subscribe) must not tear down an open workspace.
  if (previousPath === path) {
    if (isFilesModulePath(path) && root.querySelector('[data-files-workspace]')) {
      return;
    }
    if (isSedrWorkspacePath(path) && root.querySelector('[data-sedr-workspace]')) {
      return;
    }
    if (isAdminModulePath(path) && root.querySelector('[data-admin-workspace]')) {
      return;
    }
    if (isSettingsModulePath(path) && root.querySelector('[data-settings-workspace]')) {
      return;
    }
    if (path.startsWith('/information') && root.querySelector('.module-page--info')) {
      return;
    }
  }

  root.replaceChildren(AppShell(renderRoute()));
}

export function initApp(): void {
  const root = document.getElementById('app');
  if (!root) throw new Error('Missing #app root');

  stripSecretQueryParams();
  setUnauthorizedHandler(handleUnauthorized);

  let renderedPath = '';

  const render = () => {
    const path = getPath();
    const previousPath = renderedPath;
    renderedPath = path;
    applyRoute(root, previousPath);
  };

  subscribe(render);
  subscribeVaultRecoverySetup(render);
  window.addEventListener('popstate', render);
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a[href^="/"]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    const href = anchor.getAttribute('href') ?? '/';
    const revalidateSession =
      anchor.classList.contains('nav-link')
      && getAppState().kind === 'authenticated'
      && !isGuestAppPath(getPath());

    void (async () => {
      if (revalidateSession) {
        await refreshSession();
        if (getAppState().kind !== 'authenticated') {
          return;
        }
      }
      history.pushState({}, '', href);
      render();
    })();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (getAppState().kind !== 'authenticated') return;
    if (isGuestAppPath(getPath())) return;
    void refreshSession();
  });

  render();
  if (!isGuestAppPath(getPath())) {
    void refreshSession();
  }
}
